import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { createOpenAIChatCompletion } from "../../services/openaiFunctions";
import {
  AdPlatform,
  CommunityAdFormat,
  CommunityMediaConfig,
  VoiceOfCustomerQuote,
} from "../../types/CommunityMediaConfig";
import { SubscriptionLandingInfo } from "../../types/Paywall";
import {
  normalizeCommunityMediaConfig,
  buildVisualStylePromptBlock,
  buildVoiceOfCustomerPromptBlock,
} from "./mediaConfig";
import {
  generateGptImage,
  generateGeminiImage,
} from "../../components/screens/plan/dayplan/components/dailyStep/SlideGenerator.api";

// ─── Platform spec table (ad-creative skill) ─────────────────────────────

export const AD_PLATFORM_SPECS = {
  meta: {
    primaryTextVisible: 125,
    primaryTextMax: 2200,
    headline: 40,
    description: 30,
    optimalLengthSec: { min: 15, max: 30 },
    aspectRatio: "9:16 or 1:1",
  },
  tiktok: {
    adText: 80,
    adTextMax: 100,
    optimalLengthSec: { min: 9, max: 21 },
    aspectRatio: "9:16",
  },
} as const;

// ─── Placement specs (image skill — per-placement aspect ratios) ─────────
//
// gpt-image-1 supports 1024x1024 (1:1), 1024x1536 (2:3 portrait, close to
// 9:16), 1536x1024 (3:2 landscape). We map each placement to the closest
// supported size.

export type AdPlacement =
  | "meta-feed-square"
  | "meta-feed-portrait"
  | "meta-stories-reels"
  | "tiktok-feed";

export const AD_PLACEMENT_SPECS: Record<
  AdPlacement,
  {
    label: string;
    platform: AdPlatform;
    aspectRatio: string;
    /** Size string accepted by gpt-image-1. */
    gptImageSize: "1024x1024" | "1024x1536" | "1536x1024";
    /** Aspect-ratio hint for Gemini imageConfig. */
    geminiAspectRatio: "1:1" | "9:16" | "4:5";
  }
> = {
  "meta-feed-square": {
    label: "Meta feed (1:1)",
    platform: "meta",
    aspectRatio: "1:1 — 1080x1080",
    gptImageSize: "1024x1024",
    geminiAspectRatio: "1:1",
  },
  "meta-feed-portrait": {
    label: "Meta feed (4:5)",
    platform: "meta",
    aspectRatio: "4:5 — 1080x1350",
    gptImageSize: "1024x1536",
    geminiAspectRatio: "4:5",
  },
  "meta-stories-reels": {
    label: "Meta Stories / Reels (9:16)",
    platform: "meta",
    aspectRatio: "9:16 — 1080x1920",
    gptImageSize: "1024x1536",
    geminiAspectRatio: "9:16",
  },
  "tiktok-feed": {
    label: "TikTok feed (9:16)",
    platform: "tiktok",
    aspectRatio: "9:16 — 1080x1920",
    gptImageSize: "1024x1536",
    geminiAspectRatio: "9:16",
  },
};

export const PLACEMENTS_FOR_PLATFORM: Record<AdPlatform, AdPlacement[]> = {
  meta: ["meta-feed-square", "meta-feed-portrait", "meta-stories-reels"],
  tiktok: ["tiktok-feed"],
};

// ─── Types returned to the UI ─────────────────────────────────────────────

export type AdAngleCategory =
  | "pain-point"
  | "outcome"
  | "social-proof"
  | "curiosity"
  | "comparison"
  | "urgency"
  | "identity"
  | "contrarian";

export type AdScriptBeat = {
  startSec: number;
  endSec: number;
  role: "hook" | "offer" | "proof" | "demonstration" | "objection" | "cta";
  spoken?: string;
  textOverlay?: string;
  shot?: string;
};

export type AdScript = {
  platform: AdPlatform;
  formatKey: string;
  formatLabel: string;
  angle: AdAngleCategory;
  totalLengthSec: number;
  beats: AdScriptBeat[];
  /** Single spoken/voiceover block — concatenation of beat.spoken lines. */
  voiceoverScript: string;
  /** Hard CTA shown in the final beat. */
  finalCta: string;
  /** Shot direction for any static asset (e.g. founder talking head, B-roll). */
  heroShot?: string;
};

export type AdCopyVariant = {
  platform: AdPlatform;
  angle: AdAngleCategory;
  /** Meta primary text or TikTok ad text. */
  primaryText: string;
  primaryTextLen: number;
  primaryTextOverLimit: boolean;
  /** Meta only — headline (40 chars). */
  headline?: string;
  headlineLen?: number;
  headlineOverLimit?: boolean;
  /** Meta only — description (30 chars). */
  description?: string;
  descriptionLen?: number;
  descriptionOverLimit?: boolean;
  /** Final on-button CTA, e.g. "Shop Now", "Sign Up". */
  cta: string;
};

// ─── Input shapes ─────────────────────────────────────────────────────────

export type GenerateAdScriptInput = {
  communityId: string;
  /** What we're advertising — product, offer, lesson, or campaign theme. */
  campaign: string;
  /** Which format from the community's adFormats library to use. */
  formatKey: string;
  /** Which platform we're producing for. */
  platform: AdPlatform;
  /** Which angle to lean into. */
  angle: AdAngleCategory;
};

export type GenerateAdCopyVariantsInput = {
  communityId: string;
  campaign: string;
  /** Which platforms to produce for. */
  platforms: AdPlatform[];
  /** How many variations per angle. Defaults to 3. */
  variationsPerAngle?: number;
  /** Which angles to cover. Defaults to 4 core angles. */
  angles?: AdAngleCategory[];
};

// ─── Community config loader (reuses the same mediaConfig doc) ────────────

const loadAdContext = async (
  communityId: string
): Promise<{
  community: Record<string, unknown>;
  mediaConfig: CommunityMediaConfig;
  landingPage: SubscriptionLandingInfo | null;
}> => {
  const [communitySnap, mediaConfigSnap, landingSnap] = await Promise.all([
    firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get(),
    firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.COMMUNITY_MEDIA)
      .doc("mediaConfig")
      .get(),
    firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.LANDING_PAGE)
      .doc("info")
      .get(),
  ]);
  return {
    community: (communitySnap.data() || {}) as Record<string, unknown>,
    mediaConfig: normalizeCommunityMediaConfig(mediaConfigSnap.data()),
    landingPage: landingSnap.exists
      ? ((landingSnap.data() || null) as SubscriptionLandingInfo | null)
      : null,
  };
};

// Resolve the campaign string. If the caller didn't provide one, fall back
// to the landing page's headline + subtitle so the model still has a clear
// "what we're advertising" hook. Used by every ad generator entry point.
const resolveCampaign = (
  userCampaign: string,
  landingPage: SubscriptionLandingInfo | null
): string => {
  const trimmed = (userCampaign || "").trim();
  if (trimmed) return trimmed;
  const title = (landingPage?.title || "").trim();
  const subtitle = (landingPage?.subtitle || "").trim();
  const cta = (landingPage?.buttonText || "").trim();
  const parts: string[] = [];
  if (title) parts.push(title);
  if (subtitle) parts.push(subtitle);
  if (cta) parts.push(`Landing CTA: "${cta}".`);
  return parts.join(". ").trim();
};

// Builds a structured "the app the ad sells" block — features, plans,
// testimonials, FAQs — so the model writes copy that names real product
// features instead of generic claims.
const buildLandingPagePromptBlock = (
  landing: SubscriptionLandingInfo | null
): string => {
  if (!landing) return "";

  const lines: string[] = ["APP / COMMUNITY THIS AD SELLS (read carefully — the user lands here after clicking; mention real features by name, never invent):"];

  if (landing.title?.trim()) lines.push(`Landing headline: ${landing.title.trim()}`);
  if (landing.subtitle?.trim()) lines.push(`Subhead: ${landing.subtitle.trim()}`);
  if (landing.buttonText?.trim()) lines.push(`Primary CTA on landing: "${landing.buttonText.trim()}" — keep the ad's CTA consistent with this.`);

  const features = (landing.features || [])
    .map((f) => (typeof f === "string" ? f.trim() : ""))
    .filter(Boolean);
  if (features.length > 0) {
    lines.push("");
    lines.push("Real features (mention by name when they fit the angle):");
    features.slice(0, 12).forEach((f) => lines.push(`- ${f}`));
  }

  const plans = (landing.plans || []).filter(
    (p) => p && typeof p === "object"
  );
  if (plans.length > 0) {
    lines.push("");
    lines.push("Plans / offers (use real pricing — never invent a number):");
    plans.slice(0, 5).forEach((p) => {
      const parts = [p.title, p.price, p.subtitle].filter(Boolean).join(" — ");
      if (parts) lines.push(`- ${parts}${p.description ? ` (${p.description})` : ""}`);
    });
  }

  const testimonials = (landing.testimonials || [])
    .map((t) => {
      if (typeof t === "string") return t.trim();
      if (t && typeof t === "object") {
        const obj = t as Record<string, unknown>;
        const text =
          (typeof obj.quote === "string" && obj.quote) ||
          (typeof obj.text === "string" && obj.text) ||
          (typeof obj.message === "string" && obj.message) ||
          "";
        const name =
          (typeof obj.name === "string" && obj.name) ||
          (typeof obj.author === "string" && obj.author) ||
          "";
        if (text) return name ? `${text} — ${name}` : text;
      }
      return "";
    })
    .filter(Boolean);
  if (testimonials.length > 0) {
    lines.push("");
    lines.push("Real testimonials (use for social-proof angle — paraphrase, don't fabricate):");
    testimonials.slice(0, 6).forEach((t) => lines.push(`- "${t}"`));
  }

  const faqs = (landing.faqs || []).filter(
    (f) => f && typeof f === "object" && f.question
  );
  if (faqs.length > 0) {
    lines.push("");
    lines.push("Objections handled by FAQs (use for objection-based and comparison angles):");
    faqs.slice(0, 6).forEach((f) => {
      const a = (f.answer || "").trim().slice(0, 140);
      lines.push(`- Q: ${f.question.trim()} | A: ${a}${(f.answer || "").length > 140 ? "..." : ""}`);
    });
  }

  return lines.join("\n");
};

const findFormat = (
  mediaConfig: CommunityMediaConfig,
  formatKey: string
): CommunityAdFormat | undefined =>
  (mediaConfig.adFormats || []).find((f) => f.key === formatKey);

// ─── Prompt builders ──────────────────────────────────────────────────────

const formatVocQuotes = (quotes: VoiceOfCustomerQuote[] | undefined): string =>
  buildVoiceOfCustomerPromptBlock(
    quotes ? { quotes, notes: "" } : undefined
  );

const buildAdScriptPrompt = (params: {
  platform: AdPlatform;
  angle: AdAngleCategory;
  format: CommunityAdFormat;
  campaign: string;
  brandName: string;
  mediaConfig: CommunityMediaConfig;
  landingPage: SubscriptionLandingInfo | null;
}): string => {
  const { platform, angle, format, campaign, brandName, mediaConfig, landingPage } = params;

  const spec = AD_PLATFORM_SPECS[platform];
  const targetLen =
    format.targetLengthSec ||
    Math.round((spec.optimalLengthSec.min + spec.optimalLengthSec.max) / 2);

  const vocBlock = formatVocQuotes(mediaConfig.voiceOfCustomer?.quotes);
  const personaBlock = mediaConfig.buyerPersona?.trim()
    ? `BUYER PERSONA:\n${mediaConfig.buyerPersona.trim()}`
    : "";
  const visualStyleBlock = buildVisualStylePromptBlock(
    mediaConfig.visualStyleProfile,
    mediaConfig.visualStyle
  );
  const landingBlock = buildLandingPagePromptBlock(landingPage);

  // Per the paid-ads skill, longer (15-30s) video ads follow a 4-zone arc:
  // 0-3s Hook / 3-8s Problem / 8-20s Solution / 20-30s CTA. Shorter TikTok
  // ads compress this — hook in second 1, offer by 3, CTA at the end.
  const beatGuidance =
    platform === "tiktok" || targetLen <= 15
      ? `BEAT ZONES (tight format — ${targetLen}s):
- 0-1s: HOOK. Pattern interrupt, question, or bold statement. Visual + verbal land simultaneously.
- 1-3s: OFFER. Product / brand "${brandName}" shown clearly.
- 3-${Math.max(4, targetLen - 3)}s: PROOF / DEMONSTRATION / PAYOFF. Reference audience pain in VOC. One concrete benefit, not a list.
- ${Math.max(5, targetLen - 2)}-${targetLen}s: HARD CTA. "Tap to start free", "Get yours", "Shop now".`
      : `BEAT ZONES (paid-ads 4-zone arc — ${targetLen}s):
- 0-3s: HOOK. Pattern interrupt, question, or bold statement. Visual + verbal land simultaneously.
- 3-8s: PROBLEM. Agitate the audience's pain using their exact VOC phrasing.
- 8-${Math.max(20, targetLen - 5)}s: SOLUTION. Show product "${brandName}" delivering the outcome. Benefit, not feature list.
- ${Math.max(20, targetLen - 5)}-${targetLen}s: HARD CTA. "Tap to start free", "Get yours", "Shop now". Clear next step.`;

  return `You are a senior paid-social ad copywriter. You write SHORT video ad scripts that win on ${platform === "meta" ? "Meta (Facebook + Instagram)" : "TikTok"}.

RULES — these are non-negotiable for paid traffic:
- Hook MUST land in second 0-1 (visual + verbal at the same time). Cold scroll has zero patience.
- Product / offer / brand "${brandName}" appears by second 3, not buried.
- Final 2-3 seconds = HARD CTA. Not a quotable line, not "follow for more". An action: "Shop now", "Tap to start free", "Get yours today".
- Brand can be mentioned multiple times — the brand IS the point of a paid ad.
- Total length: ${targetLen}s (platform sweet spot ${spec.optimalLengthSec.min}-${spec.optimalLengthSec.max}s).
- Reading level: 4th grade. No jargon.
- Reuse the audience's exact phrasing (see VOC block) when it fits.
- Aspect ratio: ${spec.aspectRatio}.
- Each beat must include BOTH a spoken line (what's said on camera or voiceover) AND a text overlay (what appears on screen for sound-off viewers — they're 85% of viewers).
- Text overlays: max 2 lines, 3-5 words per line, all-caps OR sentence case (pick one).
${platform === "tiktok"
  ? "- TikTok lean: authentic, raw, unpolished, native — feels like a real person made it, not a brand."
  : "- Meta lean: production value is OK but still feels native to feed/reels, not a TV commercial."}

${beatGuidance}

ANGLE FOR THIS SCRIPT: ${angle}
Lean into this angle for the hook and the proof beats. Don't dilute it with multiple angles.

FORMAT — "${format.label}" (${format.assetType}):
${format.structure}
${format.lever ? `\nPsychological lever: ${format.lever}` : ""}

CAMPAIGN — what we're promoting:
${campaign}

${landingBlock}

${personaBlock}

${vocBlock}

${visualStyleBlock ? `VISUAL DIRECTION (apply to every beat's shot description):\n${visualStyleBlock}` : ""}

OUTPUT — return ONLY valid JSON, no markdown, no commentary, exactly this shape:

{
  "totalLengthSec": ${targetLen},
  "heroShot": "Short shot description of the dominant visual style for this ad (one sentence — applies across beats unless a beat overrides).",
  "beats": [
    {
      "startSec": 0,
      "endSec": 1,
      "role": "hook",
      "spoken": "<what the on-camera person says, or voiceover line>",
      "textOverlay": "<words on screen, max 2 lines>",
      "shot": "<one-sentence shot description for this beat>"
    },
    {
      "startSec": 1,
      "endSec": 3,
      "role": "offer",
      "spoken": "<line that establishes the product/offer>",
      "textOverlay": "<words>",
      "shot": "<shot>"
    }
    // ... continue through proof / demonstration / objection beats
    // ... FINAL beat must be role: "cta"
  ],
  "finalCta": "<one short hard CTA — e.g. 'Tap to start free' or 'Shop now'>"
}

Produce between 4 and 7 beats. The FINAL beat MUST be role: "cta" and end at exactly ${targetLen}s.`;
};

const buildAdCopyPrompt = (params: {
  platforms: AdPlatform[];
  angles: AdAngleCategory[];
  variationsPerAngle: number;
  campaign: string;
  brandName: string;
  mediaConfig: CommunityMediaConfig;
  landingPage: SubscriptionLandingInfo | null;
}): string => {
  const { platforms, angles, variationsPerAngle, campaign, brandName, mediaConfig, landingPage } =
    params;
  const vocBlock = formatVocQuotes(mediaConfig.voiceOfCustomer?.quotes);
  const personaBlock = mediaConfig.buyerPersona?.trim()
    ? `BUYER PERSONA:\n${mediaConfig.buyerPersona.trim()}`
    : "";
  const landingBlock = buildLandingPagePromptBlock(landingPage);

  const platformRules = platforms
    .map((p) => {
      if (p === "meta") {
        const meta = AD_PLATFORM_SPECS.meta;
        return `- meta: primaryText max ${meta.primaryTextVisible} chars VISIBLE (front-load the hook in first ${meta.primaryTextVisible}), headline max ${meta.headline} chars, description max ${meta.description} chars.`;
      }
      const tt = AD_PLATFORM_SPECS.tiktok;
      return `- tiktok: adText max ${tt.adText} chars (recommended). Stop at ${tt.adTextMax} max.`;
    })
    .join("\n");

  return `You are a senior paid-ads copywriter producing Meta / TikTok ad COPY variations (not video scripts).

RULES:
- Each variation = ONE angle, hit hard. Don't mix angles.
- Reuse the audience's exact phrasing (see VOC block) when it fits — they should hear themselves in the ad.
- 4th-grade reading level. Active voice. No jargon.
- Cut filler: "really", "actually", "just", "very", "literally", "the best", "leading", "top".
- Numbers and specifics beat generic claims ("save 10 hrs/week" > "save time").
- Brand "${brandName}" can be named explicitly — paid ads do this.
- For Meta primary text: front-load the hook. Assume only the first 125 chars are visible before "see more".

CHARACTER LIMITS — anything over is dead on arrival, the platform truncates without warning:
${platformRules}

ANGLES TO COVER: ${angles.join(", ")}
Produce ${variationsPerAngle} variation${variationsPerAngle === 1 ? "" : "s"} per angle per platform.

CAMPAIGN — what we're advertising:
${campaign}

${landingBlock}

${personaBlock}

${vocBlock}

OUTPUT — return ONLY valid JSON, no markdown, no commentary, exactly this shape:

{
  "variants": [
    {
      "platform": "<meta | tiktok>",
      "angle": "<one of: ${angles.join(" | ")}>",
      "primaryText": "<Meta primary text OR TikTok ad text>",
      "headline": "<Meta only — leave omitted for TikTok>",
      "description": "<Meta only — leave omitted for TikTok>",
      "cta": "<button CTA, e.g. 'Shop Now', 'Sign Up', 'Learn More'>"
    }
  ]
}

Produce ${platforms.length * angles.length * variationsPerAngle} total variants.`;
};

// ─── JSON parsing helpers ─────────────────────────────────────────────────

const stripCodeFence = (text: string): string =>
  text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();

const tryParseObject = (raw: string): unknown | null => {
  let body = stripCodeFence(raw);
  if (!body.startsWith("{")) {
    const first = body.indexOf("{");
    const last = body.lastIndexOf("}");
    if (first >= 0 && last > first) body = body.slice(first, last + 1);
  }
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const VALID_BEAT_ROLES: AdScriptBeat["role"][] = [
  "hook",
  "offer",
  "proof",
  "demonstration",
  "objection",
  "cta",
];

const sanitizeBeat = (value: unknown): AdScriptBeat | null => {
  if (!isRecord(value)) return null;
  const startSec = Number(value.startSec);
  const endSec = Number(value.endSec);
  if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return null;
  const roleRaw = typeof value.role === "string" ? value.role.trim() : "";
  const role: AdScriptBeat["role"] = (VALID_BEAT_ROLES as string[]).includes(
    roleRaw
  )
    ? (roleRaw as AdScriptBeat["role"])
    : "demonstration";
  const spoken =
    typeof value.spoken === "string" && value.spoken.trim()
      ? value.spoken.trim()
      : undefined;
  const textOverlay =
    typeof value.textOverlay === "string" && value.textOverlay.trim()
      ? value.textOverlay.trim()
      : undefined;
  const shot =
    typeof value.shot === "string" && value.shot.trim()
      ? value.shot.trim()
      : undefined;
  return { startSec, endSec, role, spoken, textOverlay, shot };
};

// ─── Public APIs ──────────────────────────────────────────────────────────

export const generateAdScript = async (
  input: GenerateAdScriptInput
): Promise<AdScript> => {
  const communityId = (input.communityId || "").trim();
  if (!communityId) throw new Error("communityId is required.");
  if (!input.formatKey?.trim()) throw new Error("formatKey is required.");

  const { community, mediaConfig, landingPage } = await loadAdContext(communityId);
  const format = findFormat(mediaConfig, input.formatKey);
  if (!format) {
    throw new Error(
      `Ad format "${input.formatKey}" not found. Run "Find winning ad formats" first.`
    );
  }
  if (!format.platforms.includes(input.platform)) {
    throw new Error(
      `Ad format "${format.label}" is not configured for ${input.platform}.`
    );
  }

  const campaign = resolveCampaign(input.campaign, landingPage);
  if (!campaign) {
    throw new Error(
      "No campaign description provided and the community has no landing page set up. Either type a campaign brief or fill in landingPage.title + subtitle first."
    );
  }

  const brandName =
    (typeof community.title === "string" && community.title) || "the brand";

  const prompt = buildAdScriptPrompt({
    platform: input.platform,
    angle: input.angle,
    format,
    campaign,
    brandName,
    mediaConfig,
    landingPage,
  });

  const data = await createOpenAIChatCompletion<any>({
    model: "gpt-5",
    messages: [
      { role: "system", content: prompt },
      { role: "user", content: input.campaign.trim() },
    ],
  });

  const raw = data?.choices?.[0]?.message?.content || "";
  const parsed = tryParseObject(raw);
  if (!isRecord(parsed)) {
    throw new Error(
      `Ad script generation returned non-JSON. Raw: ${raw.slice(0, 400)}${raw.length > 400 ? "..." : ""}`
    );
  }

  const beatsIn = Array.isArray(parsed.beats) ? parsed.beats : [];
  const beats = beatsIn
    .map(sanitizeBeat)
    .filter((b): b is AdScriptBeat => b !== null)
    .sort((a, b) => a.startSec - b.startSec);

  if (beats.length === 0) {
    throw new Error("Ad script generation returned no beats.");
  }

  const totalLenRaw = Number(parsed.totalLengthSec);
  const totalLengthSec = Number.isFinite(totalLenRaw)
    ? Math.max(3, Math.min(60, Math.round(totalLenRaw)))
    : format.targetLengthSec ||
      Math.round(
        (AD_PLATFORM_SPECS[input.platform].optimalLengthSec.min +
          AD_PLATFORM_SPECS[input.platform].optimalLengthSec.max) /
          2
      );

  const finalCta =
    typeof parsed.finalCta === "string" && parsed.finalCta.trim()
      ? parsed.finalCta.trim()
      : beats[beats.length - 1].spoken || "Tap to learn more";

  const heroShot =
    typeof parsed.heroShot === "string" && parsed.heroShot.trim()
      ? parsed.heroShot.trim()
      : undefined;

  const voiceoverScript = beats
    .map((b) => b.spoken)
    .filter((s): s is string => Boolean(s))
    .join(" ");

  return {
    platform: input.platform,
    formatKey: format.key,
    formatLabel: format.label,
    angle: input.angle,
    totalLengthSec,
    beats,
    voiceoverScript,
    finalCta,
    heroShot,
  };
};

const DEFAULT_ANGLES: AdAngleCategory[] = [
  "pain-point",
  "outcome",
  "social-proof",
  "curiosity",
];

const VALID_ANGLES: AdAngleCategory[] = [
  "pain-point",
  "outcome",
  "social-proof",
  "curiosity",
  "comparison",
  "urgency",
  "identity",
  "contrarian",
];

const sanitizeCopyVariant = (
  value: unknown
): AdCopyVariant | null => {
  if (!isRecord(value)) return null;
  const platformRaw =
    typeof value.platform === "string" ? value.platform.trim().toLowerCase() : "";
  if (platformRaw !== "meta" && platformRaw !== "tiktok") return null;
  const platform = platformRaw as AdPlatform;

  const angleRaw = typeof value.angle === "string" ? value.angle.trim() : "";
  const angle: AdAngleCategory = (VALID_ANGLES as string[]).includes(angleRaw)
    ? (angleRaw as AdAngleCategory)
    : "pain-point";

  const primaryText =
    typeof value.primaryText === "string" ? value.primaryText.trim() : "";
  if (!primaryText) return null;

  const cta =
    typeof value.cta === "string" && value.cta.trim()
      ? value.cta.trim()
      : "Learn More";

  const spec = AD_PLATFORM_SPECS[platform];
  const primaryLimit =
    platform === "meta"
      ? AD_PLATFORM_SPECS.meta.primaryTextVisible
      : AD_PLATFORM_SPECS.tiktok.adText;

  const variant: AdCopyVariant = {
    platform,
    angle,
    primaryText,
    primaryTextLen: primaryText.length,
    primaryTextOverLimit: primaryText.length > primaryLimit,
    cta,
  };

  if (platform === "meta") {
    const headline =
      typeof value.headline === "string" && value.headline.trim()
        ? value.headline.trim()
        : undefined;
    const description =
      typeof value.description === "string" && value.description.trim()
        ? value.description.trim()
        : undefined;
    if (headline) {
      variant.headline = headline;
      variant.headlineLen = headline.length;
      variant.headlineOverLimit =
        headline.length > AD_PLATFORM_SPECS.meta.headline;
    }
    if (description) {
      variant.description = description;
      variant.descriptionLen = description.length;
      variant.descriptionOverLimit =
        description.length > AD_PLATFORM_SPECS.meta.description;
    }
  }

  // spec used for primaryTextMax/adTextMax guard, kept around for future
  // hard-truncation logic. Touch it so it's not flagged unused.
  void spec;

  return variant;
};

export const generateAdCopyVariants = async (
  input: GenerateAdCopyVariantsInput
): Promise<AdCopyVariant[]> => {
  const communityId = (input.communityId || "").trim();
  if (!communityId) throw new Error("communityId is required.");
  const platforms = (input.platforms || []).filter(
    (p): p is AdPlatform => p === "meta" || p === "tiktok"
  );
  if (platforms.length === 0) {
    throw new Error("At least one platform is required (meta or tiktok).");
  }

  const { community, mediaConfig, landingPage } = await loadAdContext(communityId);
  const campaign = resolveCampaign(input.campaign, landingPage);
  if (!campaign) {
    throw new Error(
      "No campaign description provided and the community has no landing page set up. Either type a campaign brief or fill in landingPage.title + subtitle first."
    );
  }
  const brandName =
    (typeof community.title === "string" && community.title) || "the brand";

  const angles = input.angles?.length ? input.angles : DEFAULT_ANGLES;
  const variationsPerAngle = Math.max(1, Math.min(5, input.variationsPerAngle || 3));

  const prompt = buildAdCopyPrompt({
    platforms,
    angles,
    variationsPerAngle,
    campaign,
    brandName,
    mediaConfig,
    landingPage,
  });

  const data = await createOpenAIChatCompletion<any>({
    model: "gpt-5",
    messages: [
      { role: "system", content: prompt },
      { role: "user", content: campaign },
    ],
  });

  const raw = data?.choices?.[0]?.message?.content || "";
  const parsed = tryParseObject(raw);
  if (!isRecord(parsed)) {
    throw new Error(
      `Ad copy generation returned non-JSON. Raw: ${raw.slice(0, 400)}${raw.length > 400 ? "..." : ""}`
    );
  }

  const variantsIn = Array.isArray(parsed.variants) ? parsed.variants : [];
  const variants = variantsIn
    .map(sanitizeCopyVariant)
    .filter((v): v is AdCopyVariant => v !== null);

  if (variants.length === 0) {
    throw new Error("Ad copy generation returned no variants.");
  }

  return variants;
};

// ─── Ad image generation (image + paid-ads skills) ───────────────────────

export type GenerateAdImageInput = {
  communityId: string;
  campaign: string;
  formatKey: string;
  platform: AdPlatform;
  placement: AdPlacement;
  angle: AdAngleCategory;
  /** Override the default image provider for this call. */
  provider?: "gpt" | "gemini";
  /** Gemini API key (required if provider="gemini"). */
  geminiApiKey?: string;
  /** Optional face-anchor reference (data URL). Reuses the personRef pipeline. */
  personReferenceImage?: string;
  /** Optional style-anchor references. */
  styleReferenceImages?: string[];
  /**
   * Optional specific hook / message this ad variant is communicating.
   * When present, the image prompt is biased toward visually supporting
   * THIS specific copy instead of being a generic format example.
   */
  variantHook?: string;
  /**
   * Bake the variant hook into the image as a clean text overlay (5-10 words
   * max). When false (default), the image is purely visual and the platform's
   * caption/headline carries the copy.
   */
  includeTextOnImage?: boolean;
  /**
   * Optional user-written short overlay (≤6 words). Takes precedence over
   * the auto-clipped variant hook when text-on-image is enabled. Use this
   * when the variant's primary text is too long or too nuanced to clip.
   */
  customOverlayText?: string;
};

const extractVisualDirectionFromBrief = (brief?: string): string => {
  if (!brief || !brief.trim()) return "";
  const match = brief.match(
    /Visual direction:\s*([\s\S]*?)(?=\n\s*(?:[A-Z][a-zA-Z /]+:|Slide\s+\d|Anti-patterns:|\d-\d+s:)|\n\s*\n|$)/i
  );
  return match ? match[1].trim() : "";
};

// Per-angle conceptual visual cues — biases the model toward a non-cliché
// interpretation of the angle. Without these, "pain-point" defaults to the
// dreaded posed-stress-at-desk stock photo.
const ANGLE_CONCEPTUAL_HINTS: Record<AdAngleCategory, string> = {
  "pain-point":
    'Show the EVIDENCE / ARTIFACTS of the pain via the environment, not a person performing the emotion. Examples: dim phone screen at 2am, 47 unread tabs, a half-built product on a laptop, crumpled drafts, a notebook with crossed-out ideas, a phone notification with zero engagement, a dashboard with flat metrics. NEVER show a posed person holding their head or rubbing their temples — that is the #1 AI slop cliché.',
  outcome:
    "Show the SPECIFIC visual outcome — a phone screen with a Stripe sale notification, a product on a real customer's phone, a chat thread saying 'just bought yours', a real dashboard with growth, a packaged digital product opened. Tangible, dated, real.",
  "social-proof":
    "Show a real artifact of proof — a phone screen with a DM saying 'this changed everything', a screenshot of a 5-star review, a stack of testimonial cards, a real customer holding the product. NOT a stock crowd photo, NOT a generic happy-customer shot.",
  curiosity:
    "Show a single intriguing object or scene that begs the question in the hook — a half-revealed phone screen, a notebook page mostly torn out, a single highlighted line in a doc, a door slightly open. The image should make people stop scrolling to wonder what's there.",
  comparison:
    "Show a literal split-frame — left/right, before/after, with/without — using REAL objects and REAL environments. Same lighting, same framing on both sides so the contrast is clean.",
  urgency:
    "Show a countdown, a price tag, a 'last spots' poster, a calendar with a date circled, a packed event door. Tangible markers of time/scarcity. NOT a clock cliché.",
  identity:
    "Show the audience member doing their actual thing — a creator at a real desk, a parent at a real kitchen, a founder in a real garage. Authentic environment, not a movie set.",
  contrarian:
    "Show the OPPOSITE of what everyone expects — if everyone shows a tidy desk, show a messy one. If everyone shows a smiling person, show a tired one. Visual subversion.",
};

// Heuristic: does the format brief or the visual direction describe a graphic /
// typography ad rather than a photo? Used to flip the prompt's medium.
const visualSignalsGraphic = (text: string): boolean => {
  if (!text) return false;
  const v = text.toLowerCase();
  const cues = [
    "graphic",
    "typography",
    "text overlay",
    "quote on",
    "quote card",
    "flat color",
    "color block",
    "infographic",
    "icon",
    "diagram",
    "chart",
    "screenshot",
    "tweet card",
    "no photos",
    "no people",
    "bold text",
    "sans-serif",
  ];
  return cues.some((c) => v.includes(c));
};

const buildAdImagePromptText = (params: {
  campaign: string;
  angle: AdAngleCategory;
  format: CommunityAdFormat;
  placementLabel: string;
  platform: AdPlatform;
  formatVisualDirection: string;
  communityVisualStyle: string;
  brandName: string;
  personRefAttached: boolean;
  styleRefCount: number;
  variantHook?: string;
  includeTextOnImage?: boolean;
  customOverlayText?: string;
}): string => {
  const {
    campaign,
    angle,
    format,
    placementLabel,
    platform,
    formatVisualDirection,
    communityVisualStyle,
    brandName,
    personRefAttached,
    styleRefCount,
    variantHook,
    includeTextOnImage,
    customOverlayText,
  } = params;

  const effectiveVisual = formatVisualDirection || communityVisualStyle;

  // Decide medium: graphic/typography ad vs photographic ad. The format brief
  // and the format key are the strongest signals. If the references are
  // attached (we can't analyze pixels here), we lean on the format's intent.
  const isGraphic =
    visualSignalsGraphic(effectiveVisual) ||
    format.assetType === "static-image" ||
    format.assetType === "carousel" ||
    visualSignalsGraphic(format.label) ||
    visualSignalsGraphic(format.key);

  // When a specific variant hook is supplied, the image should visually
  // support THIS message — not just the format's generic concept.
  const variantBlock = variantHook?.trim()
    ? `\nSPECIFIC AD COPY THIS IMAGE MUST SUPPORT (the hook viewers read alongside the visual — the image should be the visual answer to this exact line):\n"${variantHook.trim()}"\n`
    : "";

  // Pick the actual overlay string. User-written custom text wins; otherwise
  // we clip the variant hook aggressively (4 words) — image models bail or
  // garble longer phrases.
  const customOverlay = (customOverlayText || "").trim();
  const renderableOverlay = customOverlay
    ? customOverlay.split(/\s+/).slice(0, 6).join(" ")
    : (variantHook || "")
        .trim()
        .split(/\s+/)
        .slice(0, 4)
        .join(" ");

  // Text-rendering instruction using gpt-image-2 best practices (OpenAI cookbook +
  // community guides — May 2026):
  // - Put exact text in quotation marks.
  // - Use "the headline reads ..." phrasing (model treats "use as headline"
  //   as description; "reads" triggers actual text rendering).
  // - Specify font weight, style, placement, contrast explicitly.
  // - Add "no extra words, no duplicate text" constraint.
  const mandatoryTextBlock =
    includeTextOnImage && renderableOverlay
      ? `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HEADLINE TEXT — RENDER THIS EXACTLY AS SHOWN.
The headline reads: "${renderableOverlay}"

Render the text:
- In a bold sans-serif typeface (Helvetica / Inter / SF Pro feel — clean and condensed).
- Large enough to read instantly on a phone screen — about 25-40% of one axis of the frame.
- Positioned in the LOWER THIRD of the frame (or UPPER THIRD if the lower third has the hero subject), not centered over a face.
- High-contrast color: white text on a darker area / gradient scrim, OR black text on a lighter area. Add a soft scrim or color block behind the text if the background is busy.
- Spell every word exactly as written above. No abbreviations, no paraphrasing, no creative substitutions.

Constraints: no extra words, no duplicate text, no watermarks, no logos containing letters other than the brand mark, no additional captions. The above headline is the ONLY text in the image.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
      : "";

  // ─── Reference-following directive (MOST IMPORTANT — these define the look) ──
  // When refs are attached, they are the SOURCE OF TRUTH for the visual. The
  // model's job is to match them, not improve on them.
  const styleRefDirective =
    styleRefCount > 0
      ? `STYLE REFERENCES (CRITICAL — these define the look) — ${styleRefCount} competitor ad reference image(s) are attached above this prompt. They are the SOURCE OF TRUTH for the aesthetic of your output. Your job:
- MATCH the MEDIUM exactly: if the refs are graphic/typography-on-color cards, produce a graphic. If they are photographs, produce a photo. If they are mixed photo + bold typography overlay, produce mixed.
- MATCH the TYPOGRAPHY: if the refs have big bold sans-serif headlines, your output has big bold sans-serif headlines. Same font weight, same color palette, same hierarchy.
- MATCH the COLOR PALETTE: pull palette from the refs (e.g. white + red + black, dark grey + red accent). Do not invent a different palette.
- MATCH the COMPOSITION: same framing, same spacing, same use of negative space, same logo placement.
- MATCH the PRODUCTION VALUE: do NOT produce a cleaner, more polished, more "professional" version than the refs. They are the CEILING, not the floor.
- The output should look like it belongs in the same ad library as the references — same designer, same brand system.`
      : "";

  // ─── GRAPHIC branch (typography / quote-on-color / book-cover ads) ──
  if (isGraphic) {
    const graphicVariantHookForOverlay =
      !includeTextOnImage && variantHook?.trim()
        ? `\nUse this exact line as the dominant on-image headline: "${variantHook.trim()}". Keep it short — clip to ~8-10 words max for readability.`
        : "";

    return `Graphic ad ${placementLabel} that visually communicates the campaign and angle as a designed image. Output is a DESIGNED GRAPHIC (typography + flat color + optional product render or photo cutout), NOT a photo.
${mandatoryTextBlock}
CAMPAIGN — what we're advertising:
${campaign}
${variantBlock}
ANGLE (lean into this — one emotional pull, not a mix): ${angle}

CONCEPTUAL VISUAL FOR THIS ANGLE:
${ANGLE_CONCEPTUAL_HINTS[angle]}

FORMAT — "${format.label}" (${format.assetType}):
${format.structure.split("\n").slice(0, 6).join("\n")}

VISUAL DIRECTION (apply faithfully):
${effectiveVisual || `Bold typography on a flat background, brand logo top, hierarchy: headline → emphasis word → optional supporting line.`}

${styleRefDirective}

GRAPHIC AD RULES:
- This is a designed graphic. Render BOLD CLEAN TYPOGRAPHY — sans-serif, high contrast, clear hierarchy.${
      includeTextOnImage
        ? ' The headline text is specified at the top of the prompt — render it exactly as quoted there.'
        : graphicVariantHookForOverlay
    }
- Use the brand's accent color (typically red, brand-defined) for the emphasis word only — most of the text stays neutral.
- Brand logo placement: small, top-center or top-left, taken from the references' style. Brand: "${brandName}".
- Product render (book / app screenshot / device mockup) is OK if it fits the format and matches the refs.
- Flat / minimal background — white, off-white, or dark navy/black depending on the refs. No gradients unless refs use them.
- Native-to-feed: the graphic should feel designed, not amateur. Crisp letters, clean kerning, intentional spacing.

ANTI-PATTERNS — instant-kills:
- Photographic backgrounds or photo-heavy compositions when the refs are pure typography.
- Cluttered layouts with too many elements competing.
- Misaligned text, sloppy kerning, broken letterforms.
- Overuse of gradients, drop shadows, or 3D effects unless the refs use them.
- Stock-photo aesthetic of any kind.
- Different font family or weight than the references.

ABSOLUTE RULES:
- ${placementLabel} aspect ratio. Hold this exactly.
- Text IS ALLOWED and EXPECTED in this graphic. Render it cleanly and legibly. ${
      includeTextOnImage
        ? "Render ONLY the headline specified at the top of the prompt, exactly as quoted. No extra words, no duplicate text, no other captions."
        : "No watermarks, no source URLs, no captions outside the design itself."
    }`;
  }

  // ─── PHOTO branch (UGC / lifestyle / before-after / product-in-scene) ──
  const personRule = personRefAttached
    ? `When the scene includes the human subject, render the person from the FIRST reference image — match their face, identity, build, and clothing style faithfully. Do not invent a different face.`
    : `Default to NO person at all — environment, objects, hands, or phone screens land harder than a posed person. If a person IS necessary for the angle, render them mid-action (typing, drinking coffee, looking at phone) and never centered facing camera. Off-axis, partial, behind-the-shoulder framing wins.`;

  const platformPolish =
    platform === "tiktok"
      ? `PLATFORM = TIKTOK: phone-shot aesthetic mandatory — slight motion blur, imperfect framing, mixed natural+practical light, real-environment clutter. If the output looks like it could be a stock photo, it WILL fail. Lean toward "real person filmed this on their phone right now".`
      : `PLATFORM = META (Facebook + Instagram): native-to-feed aesthetic — looks like a real post, not a brand ad. Slightly more polish than TikTok is OK (better light, cleaner color) but stock-photo aesthetic still kills it. Lean toward "a creator's iPhone photo" not "a marketing team's hero shot".`;

  return `Photographic ${placementLabel} ad still that visually communicates the campaign and angle in a single frame.
${mandatoryTextBlock}
CAMPAIGN — what we're advertising:
${campaign}
${variantBlock}
ANGLE (lean into this — one emotional pull, not a mix): ${angle}

CONCEPTUAL VISUAL FOR THIS ANGLE:
${ANGLE_CONCEPTUAL_HINTS[angle]}

FORMAT — "${format.label}" (${format.assetType}):
${format.structure.split("\n").slice(0, 6).join("\n")}

VISUAL DIRECTION (apply faithfully):
${effectiveVisual || `Native, authentic, social-feed-appropriate. Not over-produced.`}

${styleRefDirective}

${platformPolish}

PAID AD COMPOSITION RULES:
- The visual must communicate the ANGLE${variantHook ? " AND the specific hook above" : ""} in under 1 second of viewing.
- ${personRule}
- Native feel beats polished — this should feel like a real post in the feed.
- Mention brand "${brandName}" via context (logo on shirt, app UI on phone, sticker), NEVER as a banner.
- Leave breathing room near edges for the platform's safe zones.

ANTI-PATTERNS — instant-kills, NEVER produce them:
- Posed person holding their head / rubbing temples / clutching forehead (the dreaded stress trope).
- Person looking directly at camera with a forced expression.
- Clean composed desk hero shot with a neat stack of papers, a coffee cup, a plant, and a laptop.
- Studio portrait lighting (soft key + warm fill) on what should be a candid scene.
- "Successful businessperson smiling at laptop" stock-photo aesthetic.
- 50/50 centered framing where the subject is dead-center looking forward.
- Generic "before/after fitness" or "before/after office" tropes.
- Overly saturated colors, glossy magazine finish, retouched skin.
- Stock-photo background (perfect plant, perfect lighting, perfect sticky notes).
- Any composition that could plausibly appear on Shutterstock.

ABSOLUTE RULES:
- ${placementLabel} aspect ratio. Hold this exactly.
${
  includeTextOnImage
    ? '- Render ONLY the headline specified at the top of the prompt, exactly as quoted. No other text, no captions, no duplicates, no watermarks.'
    : "- Do not include any text, letters, words, numbers, typography, captions, watermarks, or writing of any kind in the image. The platform overlay handles text. Pure visual only."
}`;
};

export const generateAdImage = async (
  input: GenerateAdImageInput
): Promise<string> => {
  const communityId = (input.communityId || "").trim();
  if (!communityId) throw new Error("communityId is required.");
  if (!input.formatKey?.trim()) throw new Error("formatKey is required.");

  const { community, mediaConfig, landingPage } = await loadAdContext(communityId);
  const format = findFormat(mediaConfig, input.formatKey);
  if (!format) {
    throw new Error(
      `Ad format "${input.formatKey}" not found. Run "Find winning ad formats" first.`
    );
  }
  if (!format.platforms.includes(input.platform)) {
    throw new Error(
      `Ad format "${format.label}" is not configured for ${input.platform}.`
    );
  }

  const placementSpec = AD_PLACEMENT_SPECS[input.placement];
  if (!placementSpec) {
    throw new Error(`Unknown placement "${input.placement}".`);
  }
  if (placementSpec.platform !== input.platform) {
    throw new Error(
      `Placement "${input.placement}" doesn't belong to platform "${input.platform}".`
    );
  }

  const campaign = resolveCampaign(input.campaign, landingPage);
  if (!campaign) {
    throw new Error(
      "No campaign description provided and the community has no landing page set up. Either type a campaign brief or fill in landingPage.title + subtitle first."
    );
  }

  const brandName =
    (typeof community.title === "string" && community.title) || "the brand";

  const formatVisualDirection = extractVisualDirectionFromBrief(format.structure);
  const communityVisualStyle = buildVisualStylePromptBlock(
    mediaConfig.visualStyleProfile,
    mediaConfig.visualStyle
  );

  const promptText = buildAdImagePromptText({
    campaign,
    angle: input.angle,
    format,
    placementLabel: placementSpec.label,
    platform: input.platform,
    formatVisualDirection,
    communityVisualStyle,
    brandName,
    personRefAttached: Boolean(input.personReferenceImage),
    styleRefCount: (input.styleReferenceImages || []).length,
    variantHook: input.variantHook,
    includeTextOnImage: input.includeTextOnImage,
    customOverlayText: input.customOverlayText,
  });

  const provider = input.provider || "gpt";

  // ─── Dev log — surfaces the full prompt + args so we can verify exactly
  // what the image model receives. Always on for now since we're iterating.
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c[ads/image] ${provider} · ${input.platform} · ${input.placement} · ${input.angle} · ${format.label}`,
    "color:#5b8def;font-weight:bold;"
  );
  // eslint-disable-next-line no-console
  console.log("formatKey:", input.formatKey);
  // eslint-disable-next-line no-console
  console.log("includeTextOnImage:", Boolean(input.includeTextOnImage));
  // eslint-disable-next-line no-console
  console.log("customOverlayText:", input.customOverlayText || "(none)");
  // eslint-disable-next-line no-console
  console.log("variantHook:", input.variantHook || "(none)");
  // eslint-disable-next-line no-console
  console.log(
    "styleReferenceImages:",
    (input.styleReferenceImages || []).length
  );
  // eslint-disable-next-line no-console
  console.log("personReferenceImage:", input.personReferenceImage ? "yes" : "no");
  // eslint-disable-next-line no-console
  console.log("formatVisualDirection (extracted):", formatVisualDirection || "(none)");
  // eslint-disable-next-line no-console
  console.log("communityVisualStyle:", communityVisualStyle || "(none)");
  // eslint-disable-next-line no-console
  console.log("--- FULL PROMPT BELOW ---");
  // eslint-disable-next-line no-console
  console.log(promptText);
  // eslint-disable-next-line no-console
  console.groupEnd();

  if (provider === "gemini") {
    if (!input.geminiApiKey) {
      throw new Error("geminiApiKey is required when provider='gemini'.");
    }
    return generateGeminiImage(
      input.geminiApiKey,
      promptText,
      input.styleReferenceImages,
      input.personReferenceImage,
      placementSpec.geminiAspectRatio
    );
  }

  return generateGptImage(
    promptText,
    input.styleReferenceImages,
    input.personReferenceImage,
    placementSpec.gptImageSize
  );
};

// ─── Campaign naming convention (paid-ads skill) ──────────────────────────
//
// paid-ads recommends `[Platform]_[Objective]_[Audience]_[Offer]_[Date]` for
// scannable Ads Manager listings, e.g. `META_Conv_Lookalike-Customers_FreeTrial_2024Q1`.
// We don't know objective/audience here — caller supplies them via opts.

export const buildAdCampaignName = (params: {
  platform: AdPlatform;
  objective?: string;
  audience?: string;
  offer?: string;
  date?: string;
}): string => {
  const platform = params.platform === "meta" ? "META" : "TIKTOK";
  const objective = (params.objective || "Conv").replace(/[^A-Za-z0-9-]/g, "");
  const audience = (params.audience || "BroadAudience").replace(
    /[^A-Za-z0-9-]/g,
    ""
  );
  const offer = (params.offer || "Default").replace(/[^A-Za-z0-9-]/g, "");
  const now = new Date();
  const date =
    params.date ||
    `${now.getFullYear()}Q${Math.floor(now.getMonth() / 3) + 1}`;
  return [platform, objective, audience, offer, date].join("_");
};

// ─── CSV export (for Ads Manager bulk upload) ─────────────────────────────

export const adCopyVariantsToCsv = (
  variants: AdCopyVariant[],
  opts?: {
    objective?: string;
    audience?: string;
    offer?: string;
  }
): string => {
  const escape = (v: string | undefined): string => {
    if (!v) return "";
    if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  };
  const header = [
    "campaign_name",
    "platform",
    "angle",
    "primary_text",
    "primary_text_len",
    "primary_over_limit",
    "headline",
    "headline_len",
    "description",
    "description_len",
    "cta",
  ].join(",");
  const rows = variants.map((v) => {
    const campaignName = buildAdCampaignName({
      platform: v.platform,
      objective: opts?.objective,
      audience: opts?.audience,
      offer: opts?.offer,
    });
    return [
      escape(campaignName),
      v.platform,
      v.angle,
      escape(v.primaryText),
      String(v.primaryTextLen),
      String(v.primaryTextOverLimit),
      escape(v.headline),
      v.headlineLen != null ? String(v.headlineLen) : "",
      escape(v.description),
      v.descriptionLen != null ? String(v.descriptionLen) : "",
      escape(v.cta),
    ].join(",");
  });
  return [header, ...rows].join("\n");
};
